import { computed, ref, watch, onScopeDispose, type Ref } from 'vue'

interface Options {
  // How much of the list the first render covers. Enough to fill a viewport —
  // everything after it arrives before a visitor can scroll to it.
  initial?: number
  // How much each subsequent frame adds.
  step?: number
  // While false the list is collapsed back to `initial`, so the next time it
  // becomes visible the first render is small again.
  active?: Ref<boolean>
}

/**
 * Reveals a long list a chunk at a time instead of all at once.
 *
 * Mounting the code palette (159 swatch cards, ~2,000 nodes) took the whole
 * render, style and paint into the click that switched tabs: 128ms on a 4x
 * throttled desktop, and interaction latency is measured to the frame that
 * answers the click. Nothing about that work is urgent below the fold, so the
 * first frame renders what fits and each following frame adds more.
 *
 * The chunks are queued with requestAnimationFrame rather than one deferred
 * task: several small tasks leave gaps for the next click to be handled,
 * whereas one big deferred task would still block it, just off the record.
 */
export function useProgressiveList<T>(source: () => T[], { initial = 4, step = 6, active }: Options = {}) {
  const all = computed(source)

  // On the server there are no frames to spread the work over, and a partial
  // list would be a hydration mismatch — render all of it.
  const count = ref(import.meta.server ? Number.POSITIVE_INFINITY : initial)

  let frame: number | null = null

  const stop = () => {
    if (frame !== null) {
      cancelAnimationFrame(frame)
      frame = null
    }
  }

  const pump = () => {
    frame = null
    if (count.value >= all.value.length) return
    count.value += step
    frame = requestAnimationFrame(pump)
  }

  if (import.meta.client) {
    // Becoming visible is what starts a reveal, and leaving is what collapses
    // it. Nothing else resets the count: the list also changes length when a
    // color is deleted from it, and starting over there would pull rendered
    // groups back off the page under the visitor's cursor.
    watch(
      () => (active ? active.value : true),
      (isActive) => {
        stop()
        count.value = initial
        if (isActive && all.value.length > initial) frame = requestAnimationFrame(pump)
      },
      { immediate: true },
    )

    // A list that grows while it is on screen (a fresh palette arriving) keeps
    // filling in from wherever the reveal had got to.
    watch(
      () => all.value.length,
      (length) => {
        const isActive = active ? active.value : true
        if (isActive && frame === null && count.value < length) frame = requestAnimationFrame(pump)
      },
    )
  }

  onScopeDispose(stop)

  return {
    // The part of the list that should render right now.
    visible: computed(() => (count.value >= all.value.length ? all.value : all.value.slice(0, count.value))),
    // True while chunks are still arriving — for a placeholder, if one is wanted.
    revealing: computed(() => count.value < all.value.length),
  }
}
